import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  type DispositivoVideoCaptura,
  limparHdmiDeviceIdSalvo,
} from '@/shared/lib/hdmiDevices';
import { criarCadeiaAudioImersiva, type CadeiaAudioImersiva } from '@/shared/lib/immersiveAudio';
import type { PerfilAudio } from '@/shared/models/configuracoes.model';

export { HDMI_DEVICE_KEY } from '@/shared/lib/hdmiDevices';
export type { DispositivoVideoCaptura };

// ── Constantes ────────────────────────────────────────────────────────────────

export const HDMI_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width:     { ideal: 1920, min: 1280 },
  height:    { ideal: 1080, min: 720 },
  frameRate: { ideal: 60, min: 24 },
};

// Desativa todo pós-processamento WebRTC do Chromium para preservar fidelidade do sinal HDMI.
// echoCancellation/noiseSuppression/autoGainControl causam som metálico e flutuação de volume
// em sinais de placa de captura, pois esses filtros foram projetados para microfones, não AV.
const HDMI_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl:  false,
  channelCount:     2,
};

// ── Handle público ─────────────────────────────────────────────────────────────

export interface CaptureSlideHandle {
  /** Define o volume base (0–100). Combinado com ganho via GainNode Web Audio. */
  setVolume(v: number): void;
  /** Define o ganho (0–200, onde 100 = unity). Valores > 100 amplificam o sinal. */
  setGanho(g: number): void;
  /** Define o preset de realce de áudio (natural | imersivo | voz). */
  setPerfil(p: PerfilAudio): void;
}

/** Captura de janela só recebe volume do remoto. Ganho e preset ficam na prop `perfil` / cadeia HDMI. */
export interface WindowCaptureHandle {
  setVolume(v: number): void;
}

interface CaptureSlideProps {
  muted:   boolean;
  volume?: number;  // 0–100, default 100
  ganho?:  number;  // 0–200, default 100
  perfil?: PerfilAudio;  // default 'natural'
  deviceIdAtivo:          string;
  dispositivos:           DispositivoVideoCaptura[];
  onSelecionarDispositivo: (deviceId: string) => void;
}

// ── CaptureSlide ──────────────────────────────────────────────────────────────
/**
 * Exibe sinal de captura de vídeo via placa HDMI USB (getUserMedia).
 *
 * Fluxo:
 *  - Lista e deviceId vêm do Player (useHdmiCaptura) — sincronizados com Controle Remoto.
 *  - Seletor local na TV permanece como fallback (botão "Trocar entrada").
 *  - deviceId persistido em localStorage (ds:hdmi_device_id).
 *
 * Áudio:
 *  Quando muted=false, o áudio da stream é roteado por um GainNode Web Audio
 *  (MediaStreamSource → GainNode → AudioContext.destination). O elemento <video>
 *  fica muted para evitar dupla saída. Isso permite ganho > 1.0 (amplificação real)
 *  para compensar sinais HDMI fracos, algo impossível com video.volume (limite 1.0).
 */
export const CaptureSlide = forwardRef<CaptureSlideHandle, CaptureSlideProps>(
  function CaptureSlide({
    muted, volume = 100, ganho = 100, perfil = 'natural',
    deviceIdAtivo, dispositivos, onSelecionarDispositivo,
  }, ref) {
    const videoRef    = useRef<HTMLVideoElement>(null);
    const streamRef   = useRef<MediaStream | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const cadeiaRef   = useRef<CadeiaAudioImersiva | null>(null);

    // Refs para leitura síncrona sem stale closure ao (re)criar a cadeia
    const volumeRef = useRef(volume);
    const ganhoRef  = useRef(ganho);
    const perfilRef = useRef(perfil);
    useEffect(() => { volumeRef.current = volume; }, [volume]);
    useEffect(() => { ganhoRef.current  = ganho;  }, [ganho]);
    useEffect(() => {
      perfilRef.current = perfil;
      cadeiaRef.current?.setPerfil(perfil);
    }, [perfil]);

    const [selecionando, setSelecionando] = useState(false);
    const [erro,         setErro]         = useState('');

    // ── Handle imperativo ──────────────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
      setVolume(v: number) {
        volumeRef.current = v;
        cadeiaRef.current?.setVolume(v);
      },
      setGanho(g: number) {
        ganhoRef.current = g;
        cadeiaRef.current?.setGanho(g);
      },
      setPerfil(p: PerfilAudio) {
        perfilRef.current = p;
        cadeiaRef.current?.setPerfil(p);
      },
    }), []);

    // ── Inicia stream quando deviceId está definido ────────────────────────────
    useEffect(() => {
      if (!deviceIdAtivo || selecionando) return;
      // getUserMedia é assíncrono: se o cleanup rodar no meio, o stream que
      // chega depois precisa ser parado aqui — senão a placa HDMI (em geral
      // exclusiva) fica presa e a próxima captura cai em NotReadableError.
      let cancelado = false;

      async function iniciar() {
        const mediaDevices = navigator.mediaDevices;
        if (!mediaDevices || typeof mediaDevices.getUserMedia !== 'function') {
          setErro('Sinal externo indisponível neste navegador. Use a Playlist padrão.');
          return;
        }

        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;

        // Encerra AudioContext anterior antes de criar um novo
        cadeiaRef.current?.disconnect();
        cadeiaRef.current = null;
        await audioCtxRef.current?.close().catch(() => {});
        audioCtxRef.current = null;
        if (cancelado) return;

        try {
          const stream = await mediaDevices.getUserMedia({
            video: { ...HDMI_VIDEO_CONSTRAINTS, deviceId: { exact: deviceIdAtivo } },
            audio: muted ? false : HDMI_AUDIO_CONSTRAINTS,
          });
          if (cancelado) {
            stream.getTracks().forEach(t => t.stop());
            return;
          }
          streamRef.current = stream;
        } catch (err) {
          if (cancelado) return;
          const domErr = err as DOMException;
          console.error('[CaptureSlide] getUserMedia falhou.', domErr.name, domErr.message);

          if (domErr.name === 'NotFoundError' || domErr.name === 'DevicesNotFoundError') {
            limparHdmiDeviceIdSalvo();
            onSelecionarDispositivo('');
            setSelecionando(true);
            return;
          }

          setErro('Não foi possível acessar o dispositivo selecionado. Reconecte a placa e tente novamente.');
          return;
        }

        if (!videoRef.current || !streamRef.current) return;

        const track = streamRef.current.getVideoTracks()[0];
        if (track) {
          const s = track.getSettings();
          console.info(`[CaptureSlide] Stream ativo — ${s.width}x${s.height} @ ${s.frameRate}fps | deviceId: ${s.deviceId}`);
        }

        // ── Pipeline de áudio via Web Audio API ──────────────────────────────
        // Roteia o áudio da stream pela cadeia de DSP imersiva (graves, presença,
        // largura estéreo, compressor) terminando num GainNode, em vez do elemento
        // <video>. Permite gain > 1.0 para amplificar sinais HDMI fracos.
        // O <video> fica sempre muted; o áudio sai pelo AudioContext.destination.
        const audioTracks = streamRef.current.getAudioTracks();
        if (audioTracks.length > 0) {
          try {
            const ctx = new AudioContext();
            const src = ctx.createMediaStreamSource(streamRef.current);
            cadeiaRef.current = criarCadeiaAudioImersiva(ctx, src, {
              volume: volumeRef.current,
              ganho:  ganhoRef.current,
              perfil: perfilRef.current,
            });
            audioCtxRef.current = ctx;
            // Muta o elemento — áudio já sai pelo AudioContext
            videoRef.current.muted = true;
          } catch (e) {
            // Fallback: deixa o elemento de vídeo reproduzir o áudio diretamente
            console.warn('[CaptureSlide] AudioContext não disponível, usando video.volume.', e);
            videoRef.current.muted  = false;
            videoRef.current.volume = Math.min(1, (volumeRef.current / 100) * (ganhoRef.current / 100));
          }
        } else {
          videoRef.current.muted = true;
        }

        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(e => {
          console.warn('[CaptureSlide] video.play() bloqueado pelo navegador.', e);
        });
      }

      iniciar();

      return () => {
        cancelado = true;
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        cadeiaRef.current?.disconnect();
        cadeiaRef.current = null;
        audioCtxRef.current?.close().catch(() => {});
        audioCtxRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
      };
    }, [deviceIdAtivo, muted, selecionando, onSelecionarDispositivo]);

    // ── Confirma seleção de dispositivo ───────────────────────────────────────
    function handleSelecionarDispositivoLocal(deviceId: string) {
      onSelecionarDispositivo(deviceId);
      setSelecionando(false);
      setErro('');
    }

    if (!deviceIdAtivo && !selecionando && dispositivos.length > 0) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-black px-8 text-center">
          <p className="text-white text-sm font-semibold">Nenhuma entrada HDMI selecionada</p>
          <p className="text-slate-500 text-xs max-w-xs">
            Escolha a placa no Controle Remoto ou toque abaixo para selecionar na TV.
          </p>
          <button
            type="button"
            onClick={() => setSelecionando(true)}
            className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-orange-500 hover:bg-orange-400 transition-colors"
          >
            Selecionar na TV
          </button>
        </div>
      );
    }

    // ── Tela de seleção de dispositivo ────────────────────────────────────────
    if (selecionando) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-6 bg-black px-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 20h12M6 4h12M9 4v16M15 4v16" />
              </svg>
            </div>
            <div>
              <p className="text-white text-sm font-semibold">Selecionar entrada HDMI</p>
              <p className="text-slate-500 text-xs">Escolha a placa de captura USB</p>
            </div>
          </div>
          <div className="w-full max-w-sm space-y-2">
            {dispositivos.map(d => (
              <button
                key={d.deviceId}
                onClick={() => handleSelecionarDispositivoLocal(d.deviceId)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 hover:bg-orange-500/15 border border-white/10 hover:border-orange-500/40 text-left transition-all"
              >
                <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.882V15.12a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                </svg>
                <span className="text-slate-200 text-sm truncate">{d.label}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => setSelecionando(false)}
            className="text-slate-600 text-xs hover:text-slate-400 transition-colors mt-2"
          >
            Cancelar
          </button>
        </div>
      );
    }

    if (erro) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-black">
          <svg className="w-16 h-16 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.882V15.12a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
          </svg>
          <p className="text-slate-500 text-sm text-center max-w-xs">{erro}</p>
          {dispositivos.length > 1 && (
            <button
              onClick={() => setSelecionando(true)}
              className="text-orange-400 text-xs hover:text-orange-300 transition-colors"
            >
              Trocar dispositivo
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="relative w-full h-full">
        {/* Sempre muted no JSX — áudio gerenciado pelo AudioContext quando ativo */}
        <video ref={videoRef} className="w-full h-full object-contain bg-black" autoPlay playsInline muted />
        {dispositivos.length > 1 && (
          <button
            onClick={() => setSelecionando(true)}
            className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-400 hover:text-white transition-colors opacity-0 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60"
            style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
            title="Trocar entrada HDMI"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            Trocar entrada
          </button>
        )}
      </div>
    );
  }
);

// ── WindowCaptureSlide ────────────────────────────────────────────────────────

/** Constraints de vídeo para captura de janela — paridade com HDMI (1920×1080 @ 60fps). */
const WINDOW_VIDEO_CONSTRAINTS_HIGH = {
  width:     { ideal: 1920, max: 1920 },
  height:    { ideal: 1080, max: 1080 },
  frameRate: { ideal: 60, min: 30 },
  cursor:    'never',
  displaySurface: 'window',
} as MediaTrackConstraints;

const WINDOW_VIDEO_CONSTRAINTS_FALLBACK = {
  width:     { ideal: 1920, max: 1920 },
  height:    { ideal: 1080, max: 1080 },
  frameRate: { ideal: 30, min: 24 },
  cursor:    'never',
} as MediaTrackConstraints;

const WINDOW_CAPTURE_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl:  false,
};

const FPS_LOG_INTERVAL_MS = 10_000;
const FPS_DROP_THRESHOLD_MS = 50;

type WindowCaptureDisplayOptions = DisplayMediaStreamOptions & {
  monitorTypeSurfaces?: 'include' | 'exclude';
  selfBrowserSurface?:  'include' | 'exclude';
};

function buildWindowCaptureOptions(muted: boolean, video: boolean | MediaTrackConstraints): WindowCaptureDisplayOptions {
  return {
    video,
    audio: muted ? false : WINDOW_CAPTURE_AUDIO_CONSTRAINTS,
    monitorTypeSurfaces: 'exclude',
    selfBrowserSurface:  'exclude',
  };
}

async function solicitarStreamJanela(muted: boolean): Promise<MediaStream> {
  const presets: WindowCaptureDisplayOptions[] = [
    buildWindowCaptureOptions(muted, WINDOW_VIDEO_CONSTRAINTS_HIGH),
    buildWindowCaptureOptions(muted, WINDOW_VIDEO_CONSTRAINTS_FALLBACK),
    buildWindowCaptureOptions(muted, true),
  ];

  let lastErr: unknown;
  for (const options of presets) {
    try {
      return await navigator.mediaDevices.getDisplayMedia(options);
    } catch (err) {
      lastErr = err;
      if (err instanceof DOMException && err.name === 'NotAllowedError') throw err;
    }
  }
  throw lastErr;
}

async function otimizarTrackVideo(track: MediaStreamTrack): Promise<void> {
  if ('contentHint' in track) {
    (track as MediaStreamTrack & { contentHint: string }).contentHint = 'motion';
  }

  const s = track.getSettings() as MediaTrackSettings & { displaySurface?: string };
  console.info(
    `[WindowCaptureSlide] Stream negociado — ${s.width ?? '?'}x${s.height ?? '?'} @ ${s.frameRate ?? '?'}fps` +
    ` | surface: ${s.displaySurface ?? '?'}`,
  );

  if ((s.frameRate ?? 0) > 0 && (s.frameRate ?? 0) < 30) {
    try {
      await track.applyConstraints({ frameRate: { ideal: 30, min: 24 } });
      const after = track.getSettings();
      console.info(`[WindowCaptureSlide] frameRate renegociado → ${after.frameRate ?? '?'}fps`);
    } catch {
      /* constraint não suportada neste dispositivo */
    }
  }
}

function conectarStreamAoVideo(video: HTMLVideoElement, stream: MediaStream, muted: boolean): void {
  video.srcObject = stream;
  // Quando o áudio é roteado pelo Web Audio (não-muted com faixa de áudio), o
  // elemento permanece muted e o som sai pelo AudioContext. Aqui só garantimos
  // que, ao mutar, o elemento também fique mudo.
  video.muted = muted;
  video.play().catch(e => {
    console.warn('[WindowCaptureSlide] video.play() bloqueado pelo navegador.', e);
  });
}

/**
 * Captura uma janela/aba/tela do próprio navegador via getDisplayMedia().
 * Sempre exige gesto do usuário — não pode ser ativado remotamente.
 * Por isso o idle state mostra um botão físico que o operador clica na TV.
 */
export const WindowCaptureSlide = forwardRef<WindowCaptureHandle, {
  muted: boolean;
  perfil?: PerfilAudio;
  onStreamActiveChange?: (active: boolean) => void;
}>(
  function WindowCaptureSlide({
    muted,
    perfil = 'natural',
    onStreamActiveChange,
  }, ref) {
  const videoRef           = useRef<HTMLVideoElement>(null);
  const streamRef          = useRef<MediaStream | null>(null);
  const fpsCleanupRef      = useRef<(() => void) | null>(null);
  const audioCtxRef        = useRef<AudioContext | null>(null);
  const cadeiaRef          = useRef<CadeiaAudioImersiva | null>(null);
  const onStreamChangeRef  = useRef(onStreamActiveChange);
  const captureVersionRef  = useRef(0);
  useEffect(() => { onStreamChangeRef.current = onStreamActiveChange; });

  const volumeRef = useRef(100);
  const perfilRef = useRef(perfil);
  useEffect(() => {
    perfilRef.current = perfil;
    cadeiaRef.current?.setPerfil(perfil);
  }, [perfil]);

  useImperativeHandle(ref, () => ({
    setVolume(v: number) {
      volumeRef.current = v;
      cadeiaRef.current?.setVolume(v);
    },
  }), []);

  const [capturando, setCapturando] = useState(false);

  // Monta/desmonta a cadeia de áudio Web Audio para a stream de janela.
  // Quando muted=false e há faixa de áudio, roteia pelo DSP (vídeo fica muted).
  function configurarAudio(video: HTMLVideoElement, stream: MediaStream) {
    cadeiaRef.current?.disconnect();
    cadeiaRef.current = null;
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;

    if (muted || stream.getAudioTracks().length === 0) {
      video.muted = true;
      return;
    }

    try {
      const ctx = new AudioContext();
      const src = ctx.createMediaStreamSource(stream);
      cadeiaRef.current = criarCadeiaAudioImersiva(ctx, src, {
        volume: volumeRef.current,
        ganho:  100,
        perfil: perfilRef.current,
      });
      audioCtxRef.current = ctx;
      video.muted = true; // áudio sai pelo AudioContext
    } catch (e) {
      console.warn('[WindowCaptureSlide] AudioContext indisponível, usando áudio do elemento.', e);
      video.muted = false;
    }
  }

  function pararMonitorFps() {
    fpsCleanupRef.current?.();
    fpsCleanupRef.current = null;
  }

  function iniciarMonitorFps(video: HTMLVideoElement) {
    pararMonitorFps();

    let frameCount = 0;
    let dropped    = 0;
    let lastFrame  = 0;
    let rvfcId     = 0;
    let ativo      = true;

    const onFrame = (now: DOMHighResTimeStamp) => {
      if (!ativo) return;
      frameCount += 1;
      if (lastFrame > 0 && now - lastFrame > FPS_DROP_THRESHOLD_MS) dropped += 1;
      lastFrame = now;
      if (typeof video.requestVideoFrameCallback === 'function') {
        rvfcId = video.requestVideoFrameCallback(onFrame);
      }
    };

    if (typeof video.requestVideoFrameCallback === 'function') {
      rvfcId = video.requestVideoFrameCallback(onFrame);
    }

    const logInterval = window.setInterval(() => {
      if (!ativo) return;
      const track = streamRef.current?.getVideoTracks()[0];
      const settings = track?.getSettings();
      const trackFps = settings?.frameRate;
      const elapsedSec = FPS_LOG_INTERVAL_MS / 1000;
      const fpsEntregue = frameCount / elapsedSec;

      console.info(
        `[WindowCaptureSlide] fps entregue: ${fpsEntregue.toFixed(1)}` +
        (trackFps ? ` | track: ${trackFps}fps` : '') +
        ` | frames pulados (>50ms): ${dropped}` +
        ` | readyState: ${video.readyState} paused: ${video.paused}`,
      );

      frameCount = 0;
      dropped    = 0;
    }, FPS_LOG_INTERVAL_MS);

    fpsCleanupRef.current = () => {
      ativo = false;
      window.clearInterval(logInterval);
      if (typeof video.cancelVideoFrameCallback === 'function' && rvfcId) {
        video.cancelVideoFrameCallback(rvfcId);
      }
    };
  }

  function finalizarStream() {
    pararMonitorFps();
    cadeiaRef.current?.disconnect();
    cadeiaRef.current = null;
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCapturando(false);
    onStreamChangeRef.current?.(false);
  }

  async function iniciarCaptura() {
    const captureVersion = ++captureVersionRef.current;
    try {
      const stream = await solicitarStreamJanela(muted);
      if (captureVersion !== captureVersionRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      const videoTrack = stream.getVideoTracks()[0];

      streamRef.current?.getTracks().forEach(t => t.stop());
      pararMonitorFps();
      streamRef.current = stream;

      if (videoTrack) {
        await otimizarTrackVideo(videoTrack);
        if (captureVersion !== captureVersionRef.current) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        videoTrack.addEventListener('ended', () => {
          if (streamRef.current === stream) finalizarStream();
        }, { once: true });
      }

      setCapturando(true);
      onStreamChangeRef.current?.(true);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        /* usuário cancelou o picker */
        return;
      }
      console.error('[WindowCaptureSlide] getDisplayMedia falhou.', err);
    }
  }

  // Conecta stream após re-render (vídeo visível) — RVFC não dispara em elemento hidden
  useEffect(() => {
    if (!capturando || !streamRef.current || !videoRef.current) return;

    const video  = videoRef.current;
    const stream = streamRef.current;

    conectarStreamAoVideo(video, stream, muted);
    configurarAudio(video, stream);

    const onPlaying = () => {
      iniciarMonitorFps(video);
    };
    video.addEventListener('playing', onPlaying);
    if (!video.paused && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      onPlaying();
    }

    return () => {
      video.removeEventListener('playing', onPlaying);
    };
  }, [capturando, muted]);

  // Re-sincroniza áudio quando admin troca playlist ↔ janela.
  // Reconstrói a cadeia Web Audio (liga/desliga DSP) conforme o novo estado de muted.
  useEffect(() => {
    if (videoRef.current && streamRef.current) {
      configurarAudio(videoRef.current, streamRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted]);

  useEffect(() => () => {
    captureVersionRef.current += 1;
    finalizarStream();
  }, []);

  return (
    <div className="relative w-full h-full bg-black">
      {/* Vídeo sempre montado — evita race entre getDisplayMedia e commit do DOM */}
      <video
        ref={videoRef}
        className={`w-full h-full object-cover bg-black ${capturando ? '' : 'invisible pointer-events-none absolute inset-0'}`}
        style={{ transform: 'translateZ(0)' }}
        autoPlay
        playsInline
        muted={muted}
      />

      {!capturando && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-sky-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 15V5.25A2.25 2.25 0 013.75 3h16.5A2.25 2.25 0 0121 5.25z" />
              </svg>
            </div>
            <div>
              <p className="text-white text-sm font-semibold">Captura de Janela</p>
              <p className="text-slate-500 text-xs">Prefira capturar uma janela (não a tela inteira)</p>
            </div>
          </div>
          <button
            onClick={iniciarCaptura}
            className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white bg-sky-600 hover:bg-sky-500 active:scale-95 transition-all"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
            </svg>
            Iniciar captura
          </button>
        </div>
      )}

      {capturando && (
        <button
          onClick={iniciarCaptura}
          className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-400 hover:text-white transition-colors opacity-0 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          title="Trocar janela"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Trocar janela
        </button>
      )}
    </div>
  );
  },
);
